import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Loader2, Upload, CheckCircle2, XCircle, FileText } from 'lucide-react';
import { uploadPedimentosBulk } from '../lib/api';
import { toast } from 'sonner';

export function BulkPedimentoDialog({ open, onOpenChange, onSuccess }) {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [visibleToClient, setVisibleToClient] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const handleFilesPicked = (e) => {
    const list = Array.from(e.target.files || []);
    const onlyPdfs = list.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (onlyPdfs.length !== list.length) {
      toast.warning(`Se omitieron ${list.length - onlyPdfs.length} archivo(s) que no eran PDF`);
    }
    setFiles(onlyPdfs.slice(0, 50));
    if (list.length > 50) toast.warning('Máximo 50 archivos por lote — se tomaron los primeros 50');
    setResults(null);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setResults(null);
    const tid = toast.loading(`Procesando ${files.length} pedimento(s)...`);
    try {
      const res = await uploadPedimentosBulk(files, { visibleToClient });
      const data = res.data || {};
      setResults(data);
      const { ok = 0, error = 0, containers_updated_total = 0 } = data.summary || {};
      if (ok > 0 && error === 0) {
        toast.success(`Lote completo: ${ok} pedimento(s) asignados a ${containers_updated_total} contenedor(es)`, { id: tid, duration: 8000 });
      } else if (ok > 0 && error > 0) {
        toast.warning(`Parcial: ${ok} OK · ${error} con error`, { id: tid, duration: 8000 });
      } else {
        toast.error(`Todos los archivos fallaron (${error})`, { id: tid, duration: 8000 });
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir pedimentos', { id: tid });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setFiles([]);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-pedimento-dialog">
        <DialogHeader>
          <DialogTitle>Subir pedimentos en lote</DialogTitle>
          <DialogDescription>
            Selecciona varios PDFs de pedimento. Cada archivo se procesa por separado: detecta MBL,
            contenedor y referencia desde el texto del PDF y los asigna automáticamente al embarque correspondiente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bulk-pedimento-files">Archivos PDF (máx 50)</Label>
            <Input
              ref={fileInputRef}
              id="bulk-pedimento-files"
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFilesPicked}
              disabled={uploading}
              data-testid="bulk-pedimento-files-input"
            />
            {files.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="bulk-pedimento-file-count">
                {files.length} archivo(s) seleccionado(s)
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="bulk-visible-to-client"
              checked={visibleToClient}
              onCheckedChange={setVisibleToClient}
              disabled={uploading}
              data-testid="bulk-visible-to-client-switch"
            />
            <Label htmlFor="bulk-visible-to-client" className="text-sm">
              Visible para clientes
            </Label>
          </div>

          {results && (
            <div className="border border-border rounded-sm" data-testid="bulk-pedimento-results">
              <div className="bg-muted/30 px-3 py-2 border-b border-border text-xs font-medium flex gap-4">
                <span className="text-emerald-400">OK: {results.summary?.ok || 0}</span>
                <span className="text-rose-400">Error: {results.summary?.error || 0}</span>
                <span className="text-muted-foreground">
                  Total contenedores asignados: {results.summary?.containers_updated_total || 0}
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {(results.results || []).map((r, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 px-3 py-2 text-xs"
                    data-testid={`bulk-pedimento-row-${idx}`}
                  >
                    {r.status === 'ok' ? (
                      <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <XCircle size={14} className="text-rose-400 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 truncate">
                        <FileText size={12} className="text-muted-foreground shrink-0" />
                        <span className="truncate font-medium">{r.filename}</span>
                      </div>
                      <div className="text-muted-foreground mt-0.5 ml-4 break-words">
                        {r.status === 'ok' ? (
                          <span>
                            {r.mbl} · Ped. <span className="font-mono">{r.pedimento_number}</span>
                            {r.reference ? ` · Ref ${r.reference}` : ''}
                            {' · '}
                            {r.containers_updated?.join(', ') || 'sin contenedores'}
                          </span>
                        ) : (
                          <span className="text-rose-400/80">{r.message}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={uploading} data-testid="bulk-pedimento-cancel-btn">
            Cerrar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!files.length || uploading}
            data-testid="bulk-pedimento-submit-btn"
          >
            {uploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload size={16} className="mr-2" />
            )}
            Subir y asignar ({files.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
